import React, { useCallback, useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Popper } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';

import AutocompleteDropDown from 'components/uiLibrary/FormInputs/AutocompleteDropDown';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';

import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from 'constants/endpointConstants';
import { AGGREGATION_TYPES, SORT_OPTION_VALUES, TP } from 'constants/index';

import { useQuery } from 'utils/react-query';

import prodQueries from 'containers/Productions/queries';
import searchQueries from 'containers/Search/queries';

import { useTranslation } from 'src/i18n';

import classes from './ProductionCountryField.module.scss';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const createCustomPopper = width =>
  React.forwardRef((props, ref) => (
    <Popper {...props} ref={ref} className={classes.popper} style={{ ...props.style, width }} />
  ));

const createCustomListbox = prefixNote =>
  React.forwardRef((props, ref) => (
    <ul ref={ref} {...props}>
      <li className={classes.prefixNote}>
        <SpriteIcon icon="info_outline" size={16} />
        <Typography size={12} color="secondary">
          {prefixNote}
        </Typography>
      </li>
      {props.children}
    </ul>
  ));

const ProductionCountryField = ({
  styles,
  countries,
  queryParams = {},
  disabled = false,
  onChange,
  trackingData = {},
  prefixNote,
  customPopperWidth,
  workId,
  showLabel = true,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const ALL_OPTION = useMemo(() => ({ id: 'ALL', name: t(`${TP}.m_ALL`), total: '', label: t(`${TP}.m_ALL`) }), [t]);

  const track = useTracking();
  const [query, setQuery] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);

  const initialCountryIds = useMemo(() => countries?.map(country => country?.entity?.id || country?.id).join(','), [
    countries,
  ]);

  const onSelectLocations = useCallback(
    locations => {
      let selectedLocations = locations?.length > 0 ? locations : [];

      if (
        selectedLocations.some(option => option.id === ALL_OPTION.id) &&
        !selectedOptions.some(option => option.id === ALL_OPTION.id)
      ) {
        selectedLocations = [ALL_OPTION];
      } else if (selectedLocations.some(option => option.id === ALL_OPTION.id) && selectedLocations.length > 1) {
        selectedLocations = selectedLocations.filter(option => option.id !== ALL_OPTION.id);
      }

      if (selectedLocations.length === 0) {
        selectedLocations = [ALL_OPTION];
      }

      setSelectedOptions(() => {
        if (onChange) {
          onChange(selectedLocations[0]?.id === ALL_OPTION.id ? [] : selectedLocations?.map(country => country));
        }

        return selectedLocations;
      });
    },
    [ALL_OPTION, onChange, selectedOptions],
  );

  useEffect(() => {
    if (!countries?.length) {
      setSelectedOptions([ALL_OPTION]);
    }
  }, [ALL_OPTION, countries]);

  useQuery(
    searchQueries.getSelectedOption({
      identifier: initialCountryIds,
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        country_id: initialCountryIds,
        aggregation_type: AGGREGATION_TYPES.COUNTRY,
      },
      queryConfig: {
        enabled: initialCountryIds?.length > 0,
        select: response => response?.data?.data,
        onSuccess: onSelectLocations,
      },
    }),
  );

  const { data: optionsData = [] } = useQuery(
    prodQueries.getAggregatedEntities({
      aggregationType: AGGREGATION_TYPES.COUNTRY,
      limit: 20,
      filters: {
        ...queryParams,
        aggregation_query: query,
        sort: SORT_OPTION_VALUES.PROD_COUNT_DESC,
      },
      queryConfig: {
        enabled: !!workId,
        select: response => response?.data || [],
      },
    }),
  );

  const options = useMemo(() => [ALL_OPTION, ...optionsData], [ALL_OPTION, optionsData]);

  const ListboxComponent = useMemo(() => (prefixNote ? createCustomListbox(prefixNote) : undefined), [prefixNote]);
  const PopperComponent = useMemo(() => (customPopperWidth ? createCustomPopper(customPopperWidth) : undefined), [
    customPopperWidth,
  ]);

  return (
    <AutocompleteDropDown
      options={options}
      disabled={disabled}
      className={classnames(classes.root, { [styles?.select]: !!styles?.select })}
      getOptionLabel={option => option.name}
      filterOptions={items => items}
      value={selectedOptions}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, value) => onSelectLocations(value)}
      renderTags={() => null}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps} className={classes.option}>
            <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} size="small" />
            <Typography size={12} truncate>
              {option.name}
              {option.total !== '' ? ` (${option.total})` : ''}
            </Typography>
          </li>
        );
      }}
      renderInput={params => {
        let displayValue = '';

        if (selectedOptions.length > 0) {
          if (selectedOptions[0].id === ALL_OPTION.id) {
            displayValue = ALL_OPTION.name;
          } else {
            displayValue = selectedOptions[0].name;

            if (selectedOptions.length > 1) {
              displayValue += ` (+${selectedOptions.length - 1})`;
            }
          }
        }

        return (
          <TextField
            {...params}
            label={showLabel ? t(`${TP}.m_COUNTRY`) : ''}
            variant="outlined"
            size="small"
            disabled={disabled}
            value={isFocused ? query : displayValue}
            placeholder={isFocused ? displayValue : ''}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => {
              track.click(trackingData);
              setIsFocused(true);
            }}
            onBlur={() => setIsFocused(false)}
            inputProps={{
              ...params.inputProps,
              value: isFocused ? query : displayValue,
            }}
          />
        );
      }}
      disableCloseOnSelect
      multiple
      disableClearable
      {...(ListboxComponent ? { ListboxComponent } : {})}
      {...(customPopperWidth ? { PopperComponent } : {})}
    />
  );
};

export default ProductionCountryField;
